import React, { useCallback, useRef } from 'react'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { paths } from '@/constants/paths'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { DiscoverItemInteraction } from '@/services/Analytics/ContentInteractions'
import { findTitleProjectSlug, formatTitleLikeLandscapeImagePath, formatWatchPath } from '@/services/ContentCatalog'
import { CatalogTitleForMap, LinkViewModel, LinksViewProps } from '@/services/RenderService'
import { formatCloudinaryPath, getCloudinaryImageUrl } from '@/utils/Cloudinary/cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { LandscapeLink } from './LandscapeLink'

export function LandscapeRail(props: LinksViewProps) {
  const { asPath } = useSafeRouter()
  const { locale } = useLocale()

  // Normalize the path by removing query params and locale prefix to match against paths.watch.index
  const pathWithoutQuery = asPath.split('?')[0] ?? asPath
  const normalizedPath = locale ? pathWithoutQuery.replace(`/${locale}`, '') : pathWithoutQuery
  const isWatchLanding = normalizedPath === paths.watch.index

  const containerPosition = props.containerPosition ?? 0

  const hoverStartTimeRef = useRef<Map<string, number>>(new Map())
  const impressionTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const hasTrackedImpression = useRef<Set<string>>(new Set())

  const createInteraction = useCallback(
    (link: LinkViewModel, itemIndex: number) => {
      const contentId = link.id ?? ''
      const typename = link.metadata?.__typename as string | undefined
      const imageUrl = link.imageUrl

      if (!contentId || !typename || !imageUrl) return null

      return new DiscoverItemInteraction({
        id: contentId,
        __typename: typename as
          | 'ContentSeries'
          | 'ContentMovie'
          | 'ContentSpecial'
          | 'ContentEpisode'
          | 'ContentLivestream',
        attribution: link.attribution,
      })
        .withSurface(isWatchLanding ? 'discover' : 'generic_list')
        .withPlacement('collection', containerPosition, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(getCloudinaryImageUrl({ path: imageUrl }), 'landscape_display')
    },
    [isWatchLanding, containerPosition],
  )

  return (
    <BasicCarousel
      id={props.id}
      data-testid={`large-landscape-grid-${props.id}`}
      containerClassName="px-4 sm:px-8 md:px-12 xl:px-16 overflow-x-hidden"
      outerClassName="-my-2"
    >
      {!props.links &&
        Array(5)
          .fill(0)
          .map((_v, i) => (
            <PlaceholderMediaCard key={i} hideTextPlaceholders className="mr-4" borderRadiusClassName="rounded-lg" />
          ))}

      {props.links.map((link, itemIndex) => {
        const artPath = formatCloudinaryPath(link.imageUrl)
        const isViableRender = !!artPath
        if (!isViableRender) {
          logger().warn(
            `An item in a LandscapeRail collection cannot be rendered because it does not have a valid imageUrl.`,
            { link },
          )
          return null
        }

        return (
          <div
            key={`${link.linkUrl}-${itemIndex}`}
            className="w-2/3 min-w-[256px] max-w-[300px] shrink-0 snap-center snap-always sm:w-2/5 lg:w-3/5"
          >
            <LandscapeLink
              link={link}
              itemIndex={itemIndex}
              createInteraction={createInteraction}
              hoverStartTimeRef={hoverStartTimeRef}
              impressionTimeoutRef={impressionTimeoutRef}
              hasTrackedImpression={hasTrackedImpression}
            />
            {props.showTitleDescription && (
              <TitleAndSubtitleDisplay className="my-4" title={link.name} subtitle={link.label} clampSubtitle />
            )}
          </div>
        )
      })}
    </BasicCarousel>
  )
}

LandscapeRail.mapTitle = mapToLink

export function mapToLink(title: CatalogTitleForMap): LinkViewModel | undefined {
  const path = formatTitleLikeLandscapeImagePath(title)
  const imageUrl = path && getCloudinaryImageUrl({ path })
  const linkUrl = formatWatchPath(title)

  if (!imageUrl || !linkUrl) {
    logger().error(
      `A link view model cannot be created for this title because one or both of imageUrl and linkUrl could not be generated.`,
      { component: LandscapeRail.name, title, imageUrl, linkUrl },
    )
    return
  }

  return {
    name: title.title,
    alt: title.title,
    label: title.description.short,
    imageUrl,
    linkUrl,
    isInEarlyAccess: 'isInEarlyAccess' in title ? Boolean(title.isInEarlyAccess) : false,
    metadata: {
      __typename: title.type,
    },
    track: {
      eventName: 'Project Tile Clicked',
      payload: {
        name: title.title,
        slug: findTitleProjectSlug(title),
        label: title.description.short,
        tileType: 'landscape_rail',
        imageUrl,
        linkUrl,
      },
    },
  }
}
